/**

 * e-Source Form Library — system templates from AQ_eSource_Template_Library.json

 * plus client priority forms (pinned to top within each category).

 */



import libraryJson from "./data/AQ_eSource_Template_Library.json";

import clientPriorityJson from "./data/AQ_eSource_ClientPriorityForms.json";

import {

  createEmptyField,

  createFieldId,

  defaultOptionsForType,

  ensureUniqueFieldName,

  isChoiceWithOptions,

  slugifyFieldName,

} from "./esourceFieldTypes";



function fieldFromSpec(spec, existingFields = []) {

  const type = spec.type || "text";

  const label = spec.label || "New field";

  const id = createFieldId();

  const preferredName =

    spec.fieldName || spec.name || slugifyFieldName(label);

  const fieldName = ensureUniqueFieldName(

    preferredName,

    existingFields,

    id,

  );

  const base = createEmptyField(type, {

    id,

    label,

    fieldName,

    required: !!spec.required,

    width: spec.width,

    placeholder: spec.placeholder || "",

    helpText: spec.helpText || "",

    defaultValue: spec.defaultValue,

    maxLength: spec.maxLength ?? "",

    minValue: spec.minValue ?? "",

    maxValue: spec.maxValue ?? "",

    dontAllowFuture: !!spec.dontAllowFuture,

    dontAllowPast: !!spec.dontAllowPast,

    defaultCurrentDate: !!spec.defaultCurrentDate,

    optionsLayout: spec.optionsLayout || "stacked",

    options: isChoiceWithOptions(type)

      ? spec.options || defaultOptionsForType(type)

      : undefined,

    columns: spec.columns,

    minRows: spec.minRows,

    maxRows: spec.maxRows,

    addRowLabel: spec.addRowLabel,

    formula: spec.formula,

  });

  return base;

}



function mapLibraryType(type) {

  switch (String(type || "").toLowerCase()) {

    case "text":

      return "text";

    case "textarea":

      return "textarea";

    case "number":

      return "number";

    case "integer":

      return "integer";

    case "date":

      return "date";

    case "time":

      return "time";

    case "boolean":

    case "yes_no":

    case "yesno":

      return "yesno";

    case "dropdown":

      return "dropdown";

    case "multi_select":

      return "multi_select";

    case "checkbox_group":

      return "multi_select";

    case "checklist":

      return "multi_select";

    case "user":

      return "text";

    case "file":

      return "text";

    case "signature":

      return "text";

    case "calculated":

      return "calculated";

    case "repeating_table":

      return "repeating_table";

    default:

      return "text";

  }

}



/**

 * Expand one library field into one or more builder fields.

 */

function expandLibraryField(libField, existingFields) {

  const type = String(libField.type || "text").toLowerCase();

  const label = libField.label || libField.name || "Field";

  const required = !!libField.required;

  const options = Array.isArray(libField.options)

    ? libField.options.map((o) => String(o))

    : undefined;



  if (type === "checklist" && options?.length) {

    return [

      fieldFromSpec(

        {

          type: "multi_select",

          name: libField.name,

          label,

          required,

          options,

          optionsLayout: "stacked",

          helpText: "Select all that apply.",

        },

        existingFields,

      ),

    ];

  }



  if (type === "checkbox_group" && options?.length) {

    return [

      fieldFromSpec(

        {

          type: "multi_select",

          name: libField.name,

          label,

          required,

          options,

          optionsLayout: "stacked",

        },

        existingFields,

      ),

    ];

  }



  if (type === "repeating_table") {

    const cols = Array.isArray(libField.columns) ? libField.columns : [];

    if (cols.length) {

      return [

        fieldFromSpec(

          {

            type: "repeating_table",

            name: libField.name,

            label,

            required,

            columns: cols.map((c) => ({

              label: c.label || c.name || "Column",

              fieldName: c.fieldName || c.name || slugifyFieldName(c.label || c.name),

              type: mapLibraryType(c.type || "text"),

              required: !!c.required,

              options: Array.isArray(c.options)

                ? c.options.map(String)

                : undefined,

            })),

            minRows: libField.minRows ?? 1,

            maxRows: libField.maxRows ?? 20,

            addRowLabel: libField.addRowLabel || "Add row",

          },

          existingFields,

        ),

      ];

    }

    return [

      fieldFromSpec(

        {

          type: "section",

          label,

        },

        existingFields,

      ),

      fieldFromSpec(

        {

          type: "instruction",

          label: "Enter repeating rows as free text for now.",

        },

        [...existingFields],

      ),

      fieldFromSpec(

        {

          type: "textarea",

          name: libField.name,

          label: `${label} (rows)`,

          required,

          helpText: "Add columns in field settings to use the table editor.",

        },

        [...existingFields],

      ),

    ];

  }



  const mapped = mapLibraryType(type);

  const helpBits = [];

  if (type === "multi_select") helpBits.push("Select all that apply.");

  if (type === "user") helpBits.push("Enter staff / user name.");



  return [

    fieldFromSpec(

      {

        type: mapped,

        name: libField.name,

        label,

        required,

        options: isChoiceWithOptions(mapped)

          ? options || defaultOptionsForType(mapped)

          : undefined,

        optionsLayout: "stacked",

        helpText: helpBits.join(" "),

        width: "half",

        formula: libField.formula,

      },

      existingFields,

    ),

  ];

}



function mergeLibraryTemplates() {

  const base = Array.isArray(libraryJson?.templates) ? libraryJson.templates : [];

  const priority = Array.isArray(clientPriorityJson?.templates)

    ? clientPriorityJson.templates

    : [];

  const byId = new Map();

  base.forEach((t) => {

    if (t?.templateId) byId.set(String(t.templateId), t);

  });

  priority.forEach((t) => {

    if (t?.templateId) byId.set(String(t.templateId), t);

  });

  return Array.from(byId.values());

}



function compareWithinCategory(a, b) {

  const pinA = a.pinToTop ? 0 : 1;

  const pinB = b.pinToTop ? 0 : 1;

  if (pinA !== pinB) return pinA - pinB;

  const orderA = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : 9999;

  const orderB = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : 9999;

  if (orderA !== orderB) return orderA - orderB;

  return String(a.templateName || "").localeCompare(String(b.templateName || ""));

}



export function getFormLibraryTemplates() {

  return mergeLibraryTemplates()

    .filter((t) => String(t.status || "Active").toLowerCase() === "active")

    .map((t) => ({

      templateId: t.templateId,

      formCode: t.formCode || t.templateId,

      category: t.category || "General",

      templateName: t.templateName || t.templateId,

      description: t.description || "",

      version: t.version || "1.0",

      fieldCount: Array.isArray(t.fields) ? t.fields.length : 0,

      pinToTop: !!t.pinToTop,

      sortOrder: Number.isFinite(Number(t.sortOrder)) ? Number(t.sortOrder) : 9999,

    }))

    .sort((a, b) => {

      const c = String(a.category).localeCompare(String(b.category));

      if (c !== 0) return c;

      return compareWithinCategory(a, b);

    });

}



export function getFormLibraryCategories() {

  const cats = new Set(getFormLibraryTemplates().map((t) => t.category));

  return Array.from(cats);

}



export function applyFormLibraryTemplate(templateId) {

  const pack = mergeLibraryTemplates().find(

    (t) => String(t.templateId) === String(templateId),

  );

  if (!pack) {

    return {

      fields: [],

      summary: "Form library template not found.",

    };

  }



  const fields = [];

  (pack.fields || []).forEach((f) => {

    const expanded = expandLibraryField(f, fields);

    fields.push(...expanded);

  });



  const scopeRaw = String(pack.category || "").trim();

  const allowed = [

    "Clinical Trial",

    "Inventory/Equipment/Logistics",

    "Laboratory",

    "Pharmacy/ePSF",

  ];

  const scope = allowed.includes(scopeRaw) ? scopeRaw : "Other";



  const code = String(pack.formCode || pack.templateId || "FRM01")

    .replace(/[^A-Za-z0-9]/g, "")

    .slice(0, 12);



  return {

    fields,

    code,

    name: pack.templateName || pack.templateId,

    moduleScope: scope,

    summary: `Loaded “${pack.templateName}” from Form Library (${fields.length} fields). Edit before saving.`,

  };

}


